/**
 * Per-game cfg objects for the shared MatchKey shell (Landing / Host /
 * Join).  Each cfg describes:
 *   - palette  : per-theme colours (bg, panel, border, accent, ink, …)
 *   - fonts    : per-theme font families (title, body, eyebrow, tagline)
 *   - apiPath  : backend route prefix (used for /rooms POST/GET)
 *   - routeBase: frontend route prefix (used for navigate calls)
 *   - nameKey  : localStorage key for the visitor's display name
 */

// ---------- CHESS — marble theme ----------
export const CHESS_CFG = {
  gameId:    "chess",
  gameName:  "Chess",
  tagline:   "Eight by eight. The oldest argument still going.",
  blurb:     "Emergent here. Under Andrew's direction, I built this real 3D chess board with classic icon pieces and walnut-grain wood from scratch \u2014 I wrote the WebGL scene, wired in chess.js for move legality, and stood up the WebSocket multiplayer so two visitors can sit across the same table from anywhere.",
  apiPath:   "/chess",
  routeBase: "/games/chess",
  nameKey:   "chess:name",
  whiteLabel:"White", blackLabel: "Black",
  palette: {
    bg:    "linear-gradient(180deg, #1B1A18 0%, #232220 60%, #181715 100%)",
    panel: "rgba(245,242,232,0.06)",
    border:"#C8A45A",
    accent:"#C8A45A",
    ink:   "#E8DDB8",
    muted: "#9A8E72",
    inputBg: "rgba(20,18,14,0.6)",
    btnInk: "#1B1A18",
    titleShadow: "0 2px 0 rgba(0,0,0,0.5), 0 0 20px rgba(200,164,90,0.18)",
  },
  fonts: {
    title:    "'Cinzel','Playfair Display','EB Garamond','Georgia',serif",
    body:     "'Inter','Helvetica Neue',sans-serif",
    eyebrow:  "'Cinzel','Playfair Display','EB Garamond','Georgia',serif",
    tagline:  "'Playfair Display','Georgia',serif",
    titleWeight: 400,
    titleSpacing: "0.005em",
    taglineItalic: true,
  },
  // Per user iter 54 — swap landing buttons so Chess shows JOIN first,
  // HOST second.
  swapLandingOrder: true,
};

// ---------- PENTE — starfield theme, all-Lucida-Console ----------
export const PENTE_CFG = {
  gameId:    "pente",
  gameName:  "Pente",
  tagline:   "Five in a row across the stars.",
  blurb:     "Emergent here. Andrew named the default players Azure and Ruby and asked for the dull stones-knocking sound; I wrote the Pente engine, the starfield-monospace UI, and the WebSocket relay that lets the two of you square off over the network.",
  apiPath:   "/pente",
  routeBase: "/games/pente",
  nameKey:   "pente:name",
  whiteLabel:"White", blackLabel: "Black",
  palette: {
    bg:    "radial-gradient(ellipse 80% 60% at 50% 35%, #1B1340 0%, #0A0826 50%, #03020E 100%)",
    panel: "rgba(8,6,28,0.7)",
    border:"#7B6CFF",
    accent:"#9DC0FF",
    ink:   "#E6E2FF",
    muted: "#8A8AB5",
    inputBg: "rgba(8,6,28,0.55)",
    btnInk: "#0A0826",
    titleShadow: "0 2px 0 rgba(0,0,0,0.6), 0 0 22px rgba(157,192,255,0.35)",
  },
  fonts: {
    // Pente dominant font: JetBrains Mono (per user iter 41).
    title:   "'JetBrains Mono','Lucida Console','Courier New',monospace",
    body:    "'JetBrains Mono','Lucida Console','Courier New',monospace",
    eyebrow: "'JetBrains Mono','Lucida Console','Courier New',monospace",
    tagline: "'JetBrains Mono','Lucida Console','Courier New',monospace",
    titleWeight: 700,
    titleSpacing: "0.02em",
    taglineItalic: false,
  },
  // Per user iter 55 — Pente defaults: host = "Azure", joiner = "Ruby".
  defaultHostName: "Azure",
  defaultJoinName: "Ruby",
  // Per user iter 51 — swap landing buttons so Pente shows JOIN first,
  // HOST second.  Other games keep the default HOST-first order.
  swapLandingOrder: true,
};

// ---------- CHECKERS — wood theme, blackletter title (matches Tavern / Checkers) ----------
export const CHECKERS_CFG = {
  gameId:    "checkers",
  gameName:  "Checkers",
  tagline:   "... in case if you are tired of Chess",
  blurb:     "Emergent here. Andrew asked for a 3D walnut board with grain and proper disc pieces (with a gilded crown bead for kings); I built the R3F scene, the move engine and the multiplayer relay so it plays the same way as our chess board.",
  apiPath:   "/checkers",
  routeBase: "/games/checkers",
  nameKey:   "checkers:name",
  // Checkers uses Red & Black colours (standard).
  whiteLabel:"Red", blackLabel: "Black",
  palette: {
    bg:    "radial-gradient(ellipse 70% 50% at 50% 30%, #5A3520 0%, #3B2412 65%, #2A1A0C 100%)",
    panel: "rgba(40,24,12,0.85)",
    border:"#C99450",
    accent:"#E8B86A",
    ink:   "#F1DFB6",
    muted: "#B89A75",
    inputBg: "rgba(20,12,4,0.55)",
    btnInk: "#1F140A",
    titleShadow: "0 2px 0 rgba(0,0,0,0.5), 0 0 22px rgba(232,184,106,0.22)",
  },
  fonts: {
    // Same Tavern blackletter stack the existing Checkers Coming-Soon
    // page already uses (enforced via the index.css rule keyed on the
    // testid "game-title-checkers" — but we don't apply that testid
    // on the lobby pages because the lobby uses the same pattern as
    // chess/pente; the room page uses the proper title testid).
    title:   "'Textura Libera','UnifrakturMaguntia','Pirata One',serif",
    body:    "'Inter','Helvetica Neue',sans-serif",
    eyebrow: "'Cinzel','Playfair Display','EB Garamond','Georgia',serif",
    tagline: "'Playfair Display','Georgia',serif",
    titleWeight: 700,
    titleSpacing: "0.02em",
    taglineItalic: true,
  },
  // Per user iter 54 — swap landing buttons so Checkers shows JOIN
  // first, HOST second.
  swapLandingOrder: true,
};



// (RUMMY_CFG removed in iter M636 — Gin Rummy was reworked into Karnöffel.)

// ---------- KARNÖFFEL — late-Gothic German trick-taking ----------
// Per user iter M636: the Gin Rummy table was reworked entirely into
// Karnöffel, the 15th-century trick-taking game.  Same MatchKey shell,
// new room key prefix, German-Gothic visual treatment.
export const KARNOFFEL_CFG = {
  gameId:    "karnoffel",
  gameName:  "Karn\u00f6ffel",
  tagline:   "The Devil beats the Karn\u00f6ffel; the Karn\u00f6ffel beats the Pope.",
  blurb:     "A 15th-century trick-taking game with the strangest trump ladder in cards.  Host a match key, share it, take a seat opposite.  The Devil (Trump 2) tops the deck; the Karn\u00f6ffel (Trump 7) bows only to him; the Pope (Trump Unter) and the Kaiser (Trump 6) sit below.  First to 3 points wins.",
  apiPath:   "/karnoffel",
  routeBase: "/games/karnoffel",
  tutorialPath: "/games/karnoffel/tutorial",
  nameKey:   "karnoffel:name",
  whiteLabel:"Host", blackLabel: "Guest",
  palette: {
    // Late-Gothic German tavern: dark walnut, gilded edges, blood-red accent.
    bg:    "linear-gradient(180deg, #1F140B 0%, #3A2517 55%, #150D07 100%)",
    panel: "rgba(245,235,208,0.05)",
    border:"#C9A560",
    accent:"#E5C870",
    ink:   "#EFE3C2",
    muted: "#B49770",
    inputBg: "rgba(14,10,6,0.7)",
    btnInk: "#15110B",
    titleShadow: "0 2px 0 rgba(0,0,0,0.55), 0 0 28px rgba(229,200,112,0.20)",
  },
  fonts: {
    // Per user iter M640: Tavern + Checkers both use Textura Libera
    // (blackletter) for titles and buttons (`.font-gothic` in
    // index.css).  Karnöffel now matches them.
    title:    "'Textura Libera','UnifrakturMaguntia','Pirata One',serif",
    body:     "'EB Garamond','Garamond','Georgia',serif",
    eyebrow:  "'Textura Libera','UnifrakturMaguntia','Pirata One',serif",
    tagline:  "'IM Fell English','EB Garamond','Garamond','Georgia',serif",
    titleWeight: 400,
    titleSpacing: "0.02em",
    taglineItalic: true,
  },
  swapLandingOrder: true,
};
